import type { ConnectionQuality, ConnectionQualityState, SignalLevel } from '../../types/connectionQuality';
import {
  getSignalColor,
  getSignalLabel,
  OFFLINE_SIGNAL_COLOR,
  RECONNECTING_SIGNAL_COLOR,
  UNSTABLE_SIGNAL_COLOR
} from './ConnectionQualityHelper';

export type ConnectionQualityMockPreset =
  | 'offline'
  | 'reconnecting'
  | 'measuring'
  | 'unstable'
  | '4'
  | '3'
  | '2'
  | '1';

/**
 * Design preview only (development).
 *
 * Set a preset to override live socket measurement, or `null` to disable.
 *
 * Options:
 * - '4' | '3' | '2' | '1' — connected bar levels with sample RTT
 * - 'measuring' — connected, bars not measured yet
 * - 'unstable' — connected, ping timeout
 * - 'reconnecting' — yellow bar animation
 * - 'offline' — no-internet icon
 */
export const CONNECTION_QUALITY_MOCK_PRESET: ConnectionQualityMockPreset | null = null;

const MOCK_RTTS: Record<'4' | '3' | '2' | '1', number> = {
  4: 85,
  3: 150,
  2: 280,
  1: 420
};

function buildConnectedMockState(signalLevel: SignalLevel, rttMs: number): ConnectionQualityState {
  const quality: ConnectionQuality = signalLevel >= 3 ? 'good' : 'slow';

  return {
    quality,
    signalLevel,
    signalColor: getSignalColor(signalLevel, quality),
    rttMs,
    lastMeasuredAt: Date.now(),
    label: getSignalLabel(quality, signalLevel, rttMs),
    isWarmup: false
  };
}

function buildConnectionQualityMockState(preset: ConnectionQualityMockPreset): ConnectionQualityState {
  switch (preset) {
    case 'offline':
      return {
        quality: 'offline',
        signalLevel: 1,
        signalColor: OFFLINE_SIGNAL_COLOR,
        rttMs: null,
        lastMeasuredAt: null,
        label: 'No connection to server',
        isWarmup: true
      };
    case 'reconnecting':
      return {
        quality: 'reconnecting',
        signalLevel: 1,
        signalColor: RECONNECTING_SIGNAL_COLOR,
        rttMs: null,
        lastMeasuredAt: null,
        label: 'Reconnecting…',
        isWarmup: true
      };
    case 'measuring':
      return {
        quality: 'reconnecting',
        signalLevel: 1,
        signalColor: RECONNECTING_SIGNAL_COLOR,
        rttMs: null,
        lastMeasuredAt: null,
        label: 'Measuring connection…',
        isWarmup: true
      };
    case 'unstable':
      return {
        quality: 'unstable',
        signalLevel: 1,
        signalColor: UNSTABLE_SIGNAL_COLOR,
        rttMs: null,
        lastMeasuredAt: Date.now(),
        label: 'Connection unstable — no response from server',
        isWarmup: false
      };
    case '4':
    case '3':
    case '2':
    case '1':
      return buildConnectedMockState(Number(preset) as SignalLevel, MOCK_RTTS[preset]);
    default:
      return buildConnectedMockState(4, MOCK_RTTS[4]);
  }
}

export function getConnectionQualityMockState(): ConnectionQualityState | null {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (CONNECTION_QUALITY_MOCK_PRESET === null) {
    return null;
  }

  return buildConnectionQualityMockState(CONNECTION_QUALITY_MOCK_PRESET);
}
